import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import qs from 'query-string'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery } from 'react-apollo'
import { Helmet } from 'react-helmet'
import PasswordResetError from './password-reset-error'
import PasswordResetForm from './password-reset-form'
import styles from './password-reset.css'
import Button from '../button'
import { LoadingModal } from '../loading/modal'
import { getModalLocation } from '../../lib/modal'
import { MODALS } from '../../constants'
import VALIDATE_PASSWORD_RESET_TOKEN_QUERY from '../../../graphql/queries/validate-password-reset-token.gql'

/**
 * Triggered by password reset link received in the email (e.g. https://www.neontv.co.nz/about-neon?modal=passwordReset&email=xxxx&token=xxxx).
 * Prompting new Password and Confirm password. Also handles displaying error or successful msg once the reset succeeds.
 */
export default function PasswordReset({ accountQuery }) {
  const history = useHistory()
  const location = useLocation()
  const [isRequestDone, setIsRequestDone] = useState(false)

  const {
    email,
    passwordResetToken,
    passwordForgottenLocation
  } = useMemo(() => {
    const parsedSearch = qs.parse(location && location.search)
    const parsedEmail = parsedSearch && parsedSearch.email
    const parsedToken = parsedSearch && parsedSearch.token

    return {
      email: parsedEmail || '',
      passwordResetToken: parsedToken || '',
      passwordForgottenLocation: getModalLocation(
        location,
        MODALS.qsParams.passwordForgotten
      )
    }
  }, [location])

  const { data, error } = useQuery(VALIDATE_PASSWORD_RESET_TOKEN_QUERY, {
    variables: {
      email,
      passwordResetToken
    },
    skip: !email || !passwordResetToken
  })

  // when reset is done and login button clicked, navigate to home. Otherwise after login the landing page will be `/about-neon`
  const onLoginClick = useCallback(() => {
    history.push(
      getModalLocation(
        { pathname: '/', search: qs.stringify({ email }) },
        MODALS.qsParams.login
      )
    )
  }, [history])

  if (accountQuery && accountQuery.loading) {
    return <LoadingModal />
  }

  const brand = 'NEON'
  const title = `Set password - ${brand}`
  const isTokenValid = Boolean(data && data.validatePasswordResetToken)

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      {error && (
        <PasswordResetError
          passwordForgottenLocation={passwordForgottenLocation}
        />
      )}
      {/* when token is valid, render the password reset form */}
      {isTokenValid && !isRequestDone && (
        <div className={styles.formWrapper}>
          <div className={styles.formHeader}>
            <h1 className={styles.formTitle}>Reset your password</h1>

            <span className={styles.formSubHeader}>
              Please enter your new password below.
            </span>
          </div>

          <PasswordResetForm
            email={email}
            passwordResetToken={passwordResetToken}
            setIsRequestDone={setIsRequestDone}
          />
        </div>
      )}
      {/* when reset is complete successfully, display the success msg */}
      {isRequestDone && (
        <div className={styles.confirmationWrapper}>
          <div className={styles.confirmationHeader}>
            <h1 className={styles.confirmationTitle}>Success!</h1>
          </div>

          <p className={styles.confirmationDescription}>
            Your password was reset successfully.
          </p>

          <Button
            className={styles.confirmationButton}
            text="Login"
            onClick={onLoginClick}
          />
        </div>
      )}
    </div>
  )
}

PasswordReset.propTypes = {
  accountQuery: PropTypes.shape({
    loading: PropTypes.bool
  })
}

PasswordReset.defaultProps = {
  accountQuery: null
}
